import React, { useState } from 'react';
import {
  BacktestScenario,
  FlatHeadlineMetrics,
  isSweepMetrics,
  PerThresholdMetrics,
  StrategyBreakdown,
} from '../../services/backtestScenariosService';

// ---- helpers ----------------------------------------------------------------

function fmtPct(v: number): string {
  return v.toFixed(1) + '%';
}

function fmtDollar(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1000) {
    return sign + '$' + (abs / 1000).toFixed(1) + 'k';
  }
  return sign + '$' + abs.toFixed(2);
}

function fmtDollarFull(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  return sign + '$' + abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---- sub-components ---------------------------------------------------------

const metricCardStyle: React.CSSProperties = {
  flex: '1 1 140px',
  backgroundColor: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  padding: '1rem',
  textAlign: 'center',
};

interface MetricCardProps {
  label: string;
  value: string;
  color?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, color }) => (
  <div style={metricCardStyle}>
    <div style={{ fontSize: '0.75em', color: '#94a3b8', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </div>
    <div style={{ fontSize: '1.6em', fontWeight: 700, color: color || '#f9fafb' }}>
      {value}
    </div>
  </div>
);

// ---- flat metrics row -------------------------------------------------------

const FlatMetricsRow: React.FC<{ m: FlatHeadlineMetrics }> = ({ m }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
    <MetricCard label="Trades" value={String(m.n_trades)} />
    <MetricCard
      label="Win Rate"
      value={fmtPct(m.win_rate_pct)}
      color={m.win_rate_pct >= 65 ? '#10b981' : m.win_rate_pct >= 55 ? '#fbbf24' : '#f87171'}
    />
    <MetricCard
      label="Total P&L"
      value={fmtDollar(m.total_pnl)}
      color={m.total_pnl >= 0 ? '#10b981' : '#f87171'}
    />
    <MetricCard
      label="Expectancy / Trade"
      value={fmtDollarFull(m.expectancy_per_trade)}
      color={m.expectancy_per_trade >= 0 ? '#10b981' : '#f87171'}
    />
  </div>
);

// ---- sweep metrics table ----------------------------------------------------

const SweepMetricsTable: React.FC<{
  thresholds: number[];
  perThreshold: Record<string, PerThresholdMetrics>;
  baseline: FlatHeadlineMetrics;
}> = ({ thresholds, perThreshold, baseline }) => {
  const thStyle: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    textAlign: 'right',
    color: '#94a3b8',
    fontWeight: 600,
    fontSize: '0.8em',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  };
  const tdStyle: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    textAlign: 'right',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    fontSize: '0.9em',
  };
  const tdFirst: React.CSSProperties = { ...tdStyle, textAlign: 'left', fontWeight: 600 };

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {/* Baseline row summary */}
      <div style={{
        backgroundColor: 'rgba(59,130,246,0.08)',
        border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: '6px',
        padding: '0.75rem 1rem',
        marginBottom: '1rem',
        fontSize: '0.9em',
        color: '#94a3b8',
      }}>
        Baseline (all {baseline.n_trades} trades):
        {' '}<strong style={{ color: '#f9fafb' }}>{fmtPct(baseline.win_rate_pct)} win rate</strong>,
        {' '}<strong style={{ color: '#10b981' }}>{fmtDollarFull(baseline.total_pnl)} total P&L</strong>,
        {' '}<strong style={{ color: '#10b981' }}>{fmtDollarFull(baseline.expectancy_per_trade)}/trade</strong>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
          <thead>
            <tr style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
              <th style={{ ...thStyle, textAlign: 'left' }}>Favorability Threshold</th>
              <th style={thStyle}>Taken</th>
              <th style={thStyle}>Skipped</th>
              <th style={thStyle}>Win Rate</th>
              <th style={thStyle}>Total P&L</th>
              <th style={thStyle}>Exp / Taken</th>
              <th style={thStyle}>Full-Universe Exp</th>
            </tr>
          </thead>
          <tbody>
            {thresholds.map((t) => {
              // Backend stores per_threshold keys as 2-decimal strings ("0.50", "0.60", "0.70"),
              // but thresholds_tested is a list of raw floats (0.5, 0.6, 0.7). String(0.5) yields
              // "0.5" which misses the keyed entry; toFixed(2) reproduces the storage format.
              const key = t.toFixed(2);
              const m = perThreshold[key];
              if (!m) return null;
              const betterThanBaseline = m.full_universe_expectancy > baseline.expectancy_per_trade;
              return (
                <tr key={key}>
                  <td style={tdFirst}>&gt;= {fmtPct(t * 100)}</td>
                  <td style={tdStyle}>{m.n_taken}</td>
                  <td style={{ ...tdStyle, color: '#64748b' }}>{m.skipped}</td>
                  <td style={{
                    ...tdStyle,
                    color: m.win_rate_pct >= 65 ? '#10b981' : m.win_rate_pct >= 55 ? '#fbbf24' : '#f87171',
                    fontWeight: 600,
                  }}>
                    {fmtPct(m.win_rate_pct)}
                  </td>
                  <td style={{ ...tdStyle, color: m.total_pnl >= 0 ? '#10b981' : '#f87171', fontWeight: 600 }}>
                    {fmtDollarFull(m.total_pnl)}
                  </td>
                  <td style={{ ...tdStyle, color: m.expectancy_per_taken >= 0 ? '#10b981' : '#f87171' }}>
                    {fmtDollarFull(m.expectancy_per_taken)}
                  </td>
                  <td style={{
                    ...tdStyle,
                    color: betterThanBaseline ? '#10b981' : '#f87171',
                    fontWeight: 600,
                  }}>
                    {fmtDollarFull(m.full_universe_expectancy)}
                    {' '}
                    <span style={{ fontSize: '0.8em', color: '#64748b' }}>
                      vs {fmtDollarFull(baseline.expectancy_per_trade)} base
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---- by-strategy table ------------------------------------------------------

const ByStrategyTable: React.FC<{ rows: StrategyBreakdown[] }> = ({ rows }) => {
  const sorted = [...rows].sort((a, b) => b.total_pnl - a.total_pnl);

  const thStyle: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    textAlign: 'right',
    color: '#94a3b8',
    fontWeight: 600,
    fontSize: '0.8em',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  };
  const tdStyle: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    textAlign: 'right',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    fontSize: '0.9em',
  };

  return (
    <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
            <th style={{ ...thStyle, textAlign: 'left' }}>Strategy</th>
            <th style={thStyle}>Trades</th>
            <th style={thStyle}>Win%</th>
            <th style={thStyle}>Total P&L</th>
            <th style={thStyle}>Exp / Trade</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.strategy}>
              <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 600, color: '#cbd5e1' }}>
                {row.strategy}
              </td>
              <td style={tdStyle}>{row.n}</td>
              <td style={{
                ...tdStyle,
                color: row.win_rate_pct >= 65 ? '#10b981' : row.win_rate_pct >= 55 ? '#fbbf24' : '#f87171',
                fontWeight: 600,
              }}>
                {fmtPct(row.win_rate_pct)}
              </td>
              <td style={{ ...tdStyle, color: row.total_pnl >= 0 ? '#10b981' : '#f87171', fontWeight: 600 }}>
                {fmtDollarFull(row.total_pnl)}
              </td>
              <td style={{ ...tdStyle, color: row.expectancy_per_trade >= 0 ? '#10b981' : '#f87171' }}>
                {fmtDollarFull(row.expectancy_per_trade)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---- by-outcome bars --------------------------------------------------------

const ByOutcomeSection: React.FC<{ byOutcome: Record<string, { trades: number; total_pnl: number }> }> = ({ byOutcome }) => {
  const entries = Object.entries(byOutcome).sort((a, b) => b[1].total_pnl - a[1].total_pnl);
  const maxTrades = Math.max(...entries.map(([, v]) => v.trades), 1);

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {entries.map(([outcome, { trades, total_pnl }]) => {
        const barPct = (trades / maxTrades) * 100;
        const isPositive = total_pnl >= 0;
        return (
          <div key={outcome} style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', fontSize: '0.85em' }}>
              <span style={{ color: '#cbd5e1', fontWeight: 600 }}>{outcome}</span>
              <span style={{ color: '#94a3b8' }}>
                {trades} trades &bull; <span style={{ color: isPositive ? '#10b981' : '#f87171', fontWeight: 600 }}>{fmtDollarFull(total_pnl)}</span>
              </span>
            </div>
            <div style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: barPct + '%',
                backgroundColor: isPositive ? '#10b981' : '#f87171',
                borderRadius: '3px',
                transition: 'width 0.3s',
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---- section header ---------------------------------------------------------

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 style={{
    margin: '0 0 0.75rem 0',
    color: '#cbd5e1',
    fontSize: '1em',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  }}>
    {children}
  </h3>
);

// ---- main card --------------------------------------------------------------

interface ScenarioCardProps {
  scenario: BacktestScenario;
}

export const ScenarioCard: React.FC<ScenarioCardProps> = ({ scenario }) => {
  const [caveatsOpen, setCaveatsOpen] = useState(false);

  const {
    title,
    run_date,
    hypothesis,
    methodology,
    headline_metrics,
    by_strategy,
    by_outcome,
    conclusion,
    caveats,
  } = scenario;

  return (
    <div style={{
      backgroundColor: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '12px',
      padding: '1.75rem',
    }}>
      {/* Title + run date */}
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ margin: '0 0 0.25rem 0', color: '#f9fafb', fontSize: '1.3em' }}>
          {title}
        </h2>
        <span style={{ fontSize: '0.8em', color: '#64748b' }}>
          Run: {run_date}
        </span>
      </div>

      {/* Hypothesis */}
      <div style={{ marginBottom: '1.5rem' }}>
        <SectionHeader>Hypothesis</SectionHeader>
        <blockquote style={{
          margin: 0,
          padding: '0.75rem 1rem',
          borderLeft: '3px solid #60a5fa',
          backgroundColor: 'rgba(96,165,250,0.07)',
          borderRadius: '0 6px 6px 0',
          fontStyle: 'italic',
          color: '#cbd5e1',
          fontSize: '0.95em',
          lineHeight: '1.6',
        }}>
          {hypothesis}
        </blockquote>
      </div>

      {/* Methodology */}
      <div style={{ marginBottom: '1.5rem' }}>
        <SectionHeader>Methodology</SectionHeader>
        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.9em', lineHeight: '1.6' }}>
          {methodology}
        </p>
      </div>

      {/* Headline metrics -- flat or sweep */}
      <div style={{ marginBottom: '1.5rem' }}>
        <SectionHeader>Headline Metrics</SectionHeader>
        {isSweepMetrics(headline_metrics) ? (
          <SweepMetricsTable
            thresholds={headline_metrics.thresholds_tested}
            perThreshold={headline_metrics.per_threshold}
            baseline={headline_metrics.baseline_comparison}
          />
        ) : (
          <FlatMetricsRow m={headline_metrics} />
        )}
      </div>

      {/* By-strategy table */}
      {by_strategy && by_strategy.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <SectionHeader>Performance by Strategy</SectionHeader>
          <ByStrategyTable rows={by_strategy} />
        </div>
      )}

      {/* By-outcome bars */}
      {by_outcome && Object.keys(by_outcome).length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <SectionHeader>Outcome Breakdown</SectionHeader>
          <ByOutcomeSection byOutcome={by_outcome} />
        </div>
      )}

      {/* Conclusion */}
      <div style={{ marginBottom: '1.5rem' }}>
        <SectionHeader>Conclusion</SectionHeader>
        <div style={{
          padding: '1rem 1.25rem',
          backgroundColor: 'rgba(16,185,129,0.08)',
          border: '1px solid rgba(16,185,129,0.2)',
          borderRadius: '8px',
          color: '#d1fae5',
          fontSize: '0.95em',
          lineHeight: '1.6',
        }}>
          {conclusion}
        </div>
      </div>

      {/* Caveats -- collapsible */}
      {caveats && caveats.length > 0 && (
        <div>
          <button
            onClick={() => setCaveatsOpen((prev) => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'none',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '6px',
              padding: '0.4rem 0.75rem',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.85em',
              marginBottom: caveatsOpen ? '0.75rem' : '0',
            }}
          >
            <span>{caveatsOpen ? '▾' : '▸'}</span>
            {caveatsOpen ? 'Hide' : 'Show'} caveats ({caveats.length})
          </button>

          {caveatsOpen && (
            <ul style={{
              margin: 0,
              paddingLeft: '1.5rem',
              color: '#94a3b8',
              fontSize: '0.85em',
              lineHeight: '1.6',
            }}>
              {caveats.map((c, i) => (
                <li key={i} style={{ marginBottom: '0.4rem' }}>
                  {c}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
